import { Body } from '@assembly-js/design-system';
import { t } from 'i18next';
import React, { useState } from 'react';

import { TOS_LINK } from 'src/constants/externalUrls';
import { ModalWrapper } from 'src/legacy/components/Modals';
import { ProxyPurchaseNotice } from 'src/routes/settings/plans/components/ProxyPurchaseGuard';
import {
  StudioPlanId,
  usePreviewStudioPlanChangeQuery,
} from 'src/services/api/studioCreditApi';
import { readIsProxySession } from 'src/utils/proxySession';

import { CreditTierSelect } from './CreditTierSelect';
import {
  PlanChangeBullet,
  PlanChangeModalButtons,
  PlanChangePrice,
  useStudioModalSheetProps,
} from './PlanChangeModalChrome';
import { SkeletonBar } from './PlansPageSkeleton';
import {
  formatDollars,
  getCreditSelectorOptions,
  StudioPlanSelection,
  toPlanChangePreviewRequest,
} from './studioPlanFeatures';

/**
 * The per-target marketing pitch (Figma 7122:59712): a headline over the
 * modal, and the "You will unlock" card. Copy exists only for the paid tiers
 * above Starter — a Starter target keeps the generic "Change plan" heading
 * and no unlock card until design supplies its pitch.
 */
const getUpgradePitch = (planId: StudioPlanId) => {
  switch (planId) {
    case 'professional':
      return {
        headline: t(
          ($) =>
            $.page_studio_plans.plan_change.upgrade_pitch.professional.headline,
        ),
        subheadline: t(
          ($) =>
            $.page_studio_plans.plan_change.upgrade_pitch.professional
              .subheadline,
        ),
        unlock: [
          {
            title: t(
              ($) =>
                $.page_studio_plans.plan_change.upgrade_pitch.professional
                  .unlock_1_title,
            ),
            description: t(
              ($) =>
                $.page_studio_plans.plan_change.upgrade_pitch.professional
                  .unlock_1_description,
            ),
          },
          {
            title: t(
              ($) =>
                $.page_studio_plans.plan_change.upgrade_pitch.professional
                  .unlock_2_title,
            ),
            description: t(
              ($) =>
                $.page_studio_plans.plan_change.upgrade_pitch.professional
                  .unlock_2_description,
            ),
          },
          {
            title: t(
              ($) =>
                $.page_studio_plans.plan_change.upgrade_pitch.professional
                  .unlock_3_title,
            ),
            description: t(
              ($) =>
                $.page_studio_plans.plan_change.upgrade_pitch.professional
                  .unlock_3_description,
            ),
          },
        ],
      };
    case 'advanced':
      return {
        headline: t(
          ($) =>
            $.page_studio_plans.plan_change.upgrade_pitch.advanced.headline,
        ),
        subheadline: t(
          ($) =>
            $.page_studio_plans.plan_change.upgrade_pitch.advanced.subheadline,
        ),
        unlock: [
          {
            title: t(
              ($) =>
                $.page_studio_plans.plan_change.upgrade_pitch.advanced
                  .unlock_1_title,
            ),
            description: t(
              ($) =>
                $.page_studio_plans.plan_change.upgrade_pitch.advanced
                  .unlock_1_description,
            ),
          },
          {
            title: t(
              ($) =>
                $.page_studio_plans.plan_change.upgrade_pitch.advanced
                  .unlock_2_title,
            ),
            description: t(
              ($) =>
                $.page_studio_plans.plan_change.upgrade_pitch.advanced
                  .unlock_2_description,
            ),
          },
          {
            title: t(
              ($) =>
                $.page_studio_plans.plan_change.upgrade_pitch.advanced
                  .unlock_3_title,
            ),
            description: t(
              ($) =>
                $.page_studio_plans.plan_change.upgrade_pitch.advanced
                  .unlock_3_description,
            ),
          },
        ],
      };
    default:
      return undefined;
  }
};

interface PlanChangeUpgradeModalProps {
  isOpen: boolean;
  selection: StudioPlanSelection;
  isLoading: boolean;
  onClose: () => void;
  /** Confirms the upgrade at the modal's selected TOTAL credits. */
  onConfirm: (totalCredits: number) => void;
  /**
   * The sellable credit rungs, from the plans catalog (`creditLadder`). The
   * backend validates against the same list, so the selector can never offer a
   * total the API would reject.
   */
  creditLadder: number[];
}

export const PlanChangeUpgradeModal: React.FC<PlanChangeUpgradeModalProps> = ({
  creditLadder,
  isOpen,
  selection,
  isLoading,
  onClose,
  onConfirm,
}) => {
  const sheetProps = useStudioModalSheetProps();
  const { tier, interval } = selection;
  const includedCredits = tier.includedCredits ?? tier.credits;
  // Inject the card's chosen total so the seeded selection always renders,
  // even if it's off the ladder.
  const creditOptions = getCreditSelectorOptions(
    includedCredits,
    creditLadder,
    tier.credits,
  );
  const [totalCredits, setTotalCredits] = useState(tier.credits);

  // Per-argument caching makes flipping between credit options instant; the
  // skeleton below is the loading state while a new selection previews.
  const {
    data: preview,
    isFetching: isPreviewLoading,
    isError: isPreviewError,
  } = usePreviewStudioPlanChangeQuery(
    toPlanChangePreviewRequest(selection, totalCredits),
    { skip: !isOpen },
  );

  // The preview endpoint ships together with this change; until it's
  // deployed (or on any failure) fall back to the catalog base price with no
  // due-today claim.
  const fallbackPriceCents =
    interval === 'year' && tier.annualCents !== undefined
      ? tier.annualCents
      : tier.monthlyCents;
  const fallbackSuffix =
    interval === 'year' && tier.annualCents !== undefined
      ? t(($) => $.page_studio_plans.plan_change.per_year)
      : t(($) => $.page_studio_plans.plan_change.per_month);
  const showPreview = !isPreviewError && preview !== undefined;
  const pitch = getUpgradePitch(selection.planId);

  return (
    <ModalWrapper
      open={isOpen}
      onClose={onClose}
      hasBackdrop
      hideDividers
      denseContent
      closeButton
      {...sheetProps}
      title={
        pitch?.headline ??
        t(($) => $.page_studio_plans.plan_change.change_plan_title)
      }
      description={pitch?.subheadline}
      hideActions
      isLoading={isLoading}
    >
      {/* 20px desktop rhythm, 16px on the sheet (Figma 7130:61938). */}
      <div className="flex flex-col gap-4 px-4 pb-4 sm:gap-5 sm:px-5 sm:pb-5">
        <div className="flex flex-col gap-5 rounded-lg border border-solid border-non-hover-border p-5">
          <div className="flex flex-col gap-4">
            <Body size="lg" className="font-medium text-black-heading">
              {t(($) => $.page_studio_plans.plan_change.upgrade_title, {
                plan: selection.planTitle,
              })}
            </Body>
            {isPreviewLoading ? (
              <SkeletonBar className="h-9 w-32" />
            ) : (
              <PlanChangePrice
                amount={formatDollars(
                  showPreview
                    ? preview.amountDueTodayCents
                    : fallbackPriceCents,
                )}
                suffix={
                  showPreview
                    ? t(($) => $.page_studio_plans.plan_change.due_today)
                    : fallbackSuffix
                }
              />
            )}
          </div>
          {/* Same options as the plan cards, but phrased as extras above the
              target plan's allowance — the plan itself is already the card's
              headline, so the dropdown sells only the addition. */}
          <CreditTierSelect
            options={creditOptions}
            additionalFromCredits={includedCredits}
            selectedCredits={totalCredits}
            onSelectCredits={setTotalCredits}
          />
          {interval === 'year' && (
            // Annual bills the whole year upfront, extras included, while
            // credits keep arriving monthly — the one thing about annual a
            // customer can't infer from the amount due.
            <Body size="xs" className="text-gray-small">
              {t(($) => $.page_studio_plans.plan_change.annual_billing_note)}
            </Body>
          )}
        </div>
        {pitch && (
          <div className="flex flex-col gap-4 rounded-lg border border-solid border-gray-150 bg-gray-100 p-5">
            <Body className="font-medium text-black-heading">
              {t(
                ($) =>
                  $.page_studio_plans.plan_change.upgrade_pitch.unlock_heading,
              )}
            </Body>
            <div className="flex flex-col gap-3">
              {pitch.unlock.map((point) => (
                <PlanChangeBullet key={point.title}>
                  <div className="flex flex-col gap-1">
                    <Body tag="span" className="text-black-heading">
                      {point.title}
                    </Body>
                    <Body tag="span" size="sm" className="text-gray-small">
                      {point.description}
                    </Body>
                  </div>
                </PlanChangeBullet>
              ))}
            </div>
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          <Body size="sm" className="text-gray-small">
            {t(($) => $.page_studio_plans.plan_change.downgrade_cancel_note)}
          </Body>
          <Body size="sm" className="text-gray-small">
            {t(($) => $.page_studio_plans.plan_change.agreement_prefix)}{' '}
            <a
              href={TOS_LINK}
              target="_blank"
              rel="noreferrer"
              className="text-gray-small underline underline-offset-2"
            >
              {t(($) => $.page_studio_plans.plan_change.agreement_terms)}
            </a>
          </Body>
        </div>
        <ProxyPurchaseNotice />
        <PlanChangeModalButtons
          cancelLabel={t(($) => $.page_studio_plans.plan_change.cancel)}
          confirmLabel={t(($) => $.page_studio_plans.plan_change.upgrade_cta, {
            plan: selection.planTitle,
          })}
          isLoading={isLoading}
          isConfirmDisabled={readIsProxySession()}
          onCancel={onClose}
          onConfirm={() => {
            onConfirm(totalCredits);
          }}
        />
      </div>
    </ModalWrapper>
  );
};
